import React, { createContext, useContext, useEffect } from 'react'
import useColorSettings, { ColorSettings } from '../hooks/useColorSettings'

interface ColorContextType {
  colors: ColorSettings
  loading: boolean
  error: string | null
  updateColor: (colorKey: keyof ColorSettings, colorValue: string) => Promise<{ success: boolean; error?: string }>
  updateColors: (newColors: Partial<ColorSettings>) => Promise<{ success: boolean; error?: string }>
  resetColors: () => Promise<{ success: boolean; error?: string }>
  getColor: (colorKey: keyof ColorSettings) => string
  getColorsAsCSS: () => string
  fetchColors: () => Promise<void>
  defaultColors: ColorSettings
}

const ColorContext = createContext<ColorContextType | undefined>(undefined)

export const useColors = () => {
  const context = useContext(ColorContext)
  if (context === undefined) {
    throw new Error('useColors must be used within a ColorProvider')
  }
  return context
}

interface ColorProviderProps {
  children: React.ReactNode
}

export const ColorProvider: React.FC<ColorProviderProps> = ({ children }) => {
  const colorSettings = useColorSettings()

  // Apply colors to document root on mount and when colors change
  useEffect(() => {
    if (!colorSettings.loading) {
      // Apply all colors to CSS custom properties
      const root = document.documentElement
      Object.entries(colorSettings.colors).forEach(([key, value]) => {
        const cssVariable = `--color-${key.replace(/_/g, '-')}`
        root.style.setProperty(cssVariable, value)
      })
    }
  }, [colorSettings.colors, colorSettings.loading])

  return (
    <ColorContext.Provider value={colorSettings}>
      {children}
    </ColorContext.Provider>
  )
}

export default ColorContext